import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Banner, ConfirmDialog, type BannerTone } from '@platform/ui-kit/workspace';

/**
 * The browser's `window.confirm` / `window.alert`, replaced by the kit's ConfirmDialog and
 * Banner (DESIGN.md: "the browser `confirm()` is the standard confirm dialog"; messages are
 * banners). The WORDING is the screen's own and is passed through unchanged.
 *
 *   const { confirm, notify, dialogs } = useWsDialogs();
 *   if (!(await confirm('Delete this group?'))) return;     // was: if (!confirm(...)) return;
 *   notify('Saved', 'success');                              // was: alert('Saved')
 *   …render {dialogs} once anywhere in the screen.
 *
 * `confirm` resolves true on the confirm button, false on Cancel, Escape or the scrim — the
 * same two outcomes the browser gave. A `destructive` tone fills the confirm button in the
 * error ink and starts focus on Cancel.
 */
export interface WsConfirmOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'destructive';
}

interface Pending extends WsConfirmOptions {
  message: ReactNode;
  resolve: (ok: boolean) => void;
}

interface Notice {
  id: number;
  tone: BannerTone;
  message: ReactNode;
}

/** Success and info notices leave on their own; warnings and errors stay until dismissed. */
const AUTO_DISMISS_MS = 5000;

export function useWsDialogs() {
  const [pending, setPending] = useState<Pending | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const seq = useRef(0);

  const confirm = useCallback(
    (message: ReactNode, options: WsConfirmOptions = {}) =>
      new Promise<boolean>((resolve) => setPending({ ...options, message, resolve })),
    [],
  );

  const notify = useCallback((message: ReactNode, tone: BannerTone = 'info') => {
    seq.current += 1;
    setNotice({ id: seq.current, tone, message });
  }, []);

  useEffect(() => {
    if (!notice || notice.tone === 'error' || notice.tone === 'warning') return;
    const id = notice.id;
    const t = window.setTimeout(() => setNotice((n) => (n && n.id === id ? null : n)), AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [notice]);

  const settle = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };

  const dialogs = (
    <>
      <ConfirmDialog
        open={!!pending}
        title={pending?.title ?? 'Please confirm'}
        confirmLabel={pending?.confirmLabel ?? 'OK'}
        cancelLabel={pending?.cancelLabel ?? 'Cancel'}
        tone={pending?.tone ?? 'default'}
        onConfirm={() => settle(true)}
        onClose={() => settle(false)}
      >
        <span style={{ whiteSpace: 'pre-line' }}>{pending?.message}</span>
      </ConfirmDialog>
      {notice && (
        <div className="pp-ws-notice" role={notice.tone === 'error' ? 'alert' : 'status'}>
          <Banner tone={notice.tone} onDismiss={() => setNotice(null)}>
            <span style={{ whiteSpace: 'pre-line' }}>{notice.message}</span>
          </Banner>
        </div>
      )}
    </>
  );

  return { confirm, notify, dialogs };
}
